import { z } from 'zod';

/**
 * INV-3 ACTOR STAMPS.
 *
 * Every record write carries an actor. It lives in `shared` because three
 * consumers must agree on the shape: the write path stamps it onto
 * `created_by`/`updated_by`, the event log stores it, and `meta.pending_change`
 * records who proposed a change (pending-change.md).
 *
 * MANUAL is the ONLY human source. Human precedence (INV-5, provenance.md) keys
 * on exactly that distinction, so nothing else may be treated as human - not even
 * SYSTEM, and not an AGENT write that a human approved (provenance.md Q3).
 */

export const ACTOR_SOURCES = ['MANUAL', 'API', 'IMPORT', 'WORKFLOW', 'AGENT', 'SYSTEM'] as const;

export type ActorSource = (typeof ACTOR_SOURCES)[number];

export interface Actor {
  readonly source: ActorSource;
  /** The acting user, when there is one. Required in practice for MANUAL. */
  readonly userId?: string | undefined;
  /** Free-form provenance detail: importer file name, workflow id, agent session. */
  readonly context?: string | undefined;
}

export const actorSourceSchema = z.literal([...ACTOR_SOURCES]);

export const actorSchema = z.strictObject({
  source: actorSourceSchema,
  userId: z.uuid().optional(),
  context: z.string().min(1).max(500).optional(),
});

export function isHumanActor(actor: Actor): boolean {
  return actor.source === 'MANUAL';
}

/**
 * IS THIS THE SAME PRINCIPAL (`docs/decisions/file-attachments.md` D7)?
 *
 * A file may only be attached by whoever UPLOADED it, and that rule needs a comparison the
 * two kinds of credential both answer honestly. A session actor is
 * `{ source: 'MANUAL', userId }` and an API-key actor is
 * `{ source: 'API' | 'AGENT', context: <the key's label> }` (`auth/service.ts`), so the
 * pair that identifies a caller is the SOURCE plus whichever of the two it carries.
 *
 * WHEN NEITHER CARRIES A DISCRIMINATOR, THE SOURCE IS THE IDENTITY. That case is only
 * reachable for the sources that are not credentials at all - a `SYSTEM` seed, a maintenance
 * write - because a session actor always carries a `userId` and an API-key actor always
 * carries its key's label. Answering false there would mean the write path could reserve a
 * file and then refuse to attach it to its own record, which is a rule about identity
 * refusing the one caller that has no ambiguity to resolve.
 */
export function isSamePrincipal(left: Actor, right: Actor): boolean {
  if (left.source !== right.source) return false;
  if (left.userId !== undefined || right.userId !== undefined) {
    return left.userId === right.userId;
  }
  return left.context === right.context;
}

/** The write path's own actor, for boot-time and maintenance writes. */
export const SYSTEM_ACTOR: Actor = { source: 'SYSTEM' };
